// The kanta/salkku overview's map — same shared `BuildingClusterMap` world-map mechanics
// (clustering, worst-status cluster color, building-circle click into `useInventoryFilter`) as
// the generic `InventoryMap`, but per-building circle size/color reflect this lens's open setup
// work remaining **on that building**, never €/kk optimization impact, and the legend/title copy
// is lens-supplied (docs/spec/concepts.md → "Portfolio inventory map", Konfiguraatio
// subsection). Generic `InventoryMap` is not reused unchanged: its €/kk derivation
// is not shown by any overview lens (setup or judgment) — only the tone/size inputs are swapped;
// clustering/chrome come from the same shared `BuildingClusterMap` both ride.
import { MapPin, X, Loader2 } from "lucide-react";
import { useInventoryFilter } from "@/lib/inventory-filter";
import { cardBase } from "@/lib/card-styles";
import { cn } from "@/lib/utils";
import { useTheme } from "@/lib/theme";
import type { Building } from "@/lib/api/building";
import type { OverviewCopy, PerBuildingOverview } from "@/lib/overview-lens";
import {
  BuildingClusterMap,
  type BuildingCirclePoint,
} from "@/components/map/BuildingClusterMap";
import { fitPointsForScope, inventoryScopeKey } from "@/lib/map-camera";

// Only two ranks here — Konfiguraatio's "Valmis"/"Tarvitsee huomiota" grammar has no third
// alert-level tone (unlike Etusivu's ok/warn/alert status). Index-aligned with the rank values
// below (0 = complete, 1 = incomplete).
const RANK_COLOR_VARS = ["--color-success", "--color-warning"];

const tone = {
  ok: "var(--color-success)",
  warn: "var(--color-warning)",
};

/** Circle radius in px, scaled by this building's own open-question count — the per-building
 *  swap-in for InventoryMap's €/kk radius. */
function radiusForOpenCount(openCount: number): number {
  return Math.max(6, Math.min(22, 6 + openCount * 3));
}

function toPoint(
  building: Building,
  perBuilding: Map<string, PerBuildingOverview>,
  activeId: string | null,
): BuildingCirclePoint | null {
  if (building.lat == null || building.lon == null) return null;
  const entry = perBuilding.get(building.id);
  // Not-yet-settled buildings render as complete (rank 0) until their state arrives, matching
  // the former per-city rollup's "remaining only counts a *settled* incomplete building".
  const rank = entry?.complete === false ? 1 : 0;
  return {
    id: building.id,
    lat: building.lat,
    lon: building.lon,
    rank,
    radius: radiusForOpenCount(entry?.openQuestions.length ?? 0),
    active: building.id === activeId,
  };
}

export function OverviewMap({
  scopedBuildings,
  perBuilding,
  totalBuildings,
  loading,
  copy,
}: {
  scopedBuildings: Building[];
  perBuilding: Map<string, PerBuildingOverview>;
  totalBuildings: number;
  loading: boolean;
  copy: Pick<
    OverviewCopy,
    "mapTitle" | "mapSubtitle" | "mapLegendOkLabel" | "mapLegendWarnLabel"
  >;
}) {
  const { buildingId, groupId, filter, setBuildingId, isFiltered, reset } =
    useInventoryFilter();
  const { mode, palette } = useTheme();

  const points = scopedBuildings
    .map((b) => toPoint(b, perBuilding, buildingId))
    .filter((p): p is BuildingCirclePoint => p !== null);
  const missingCoordCount = scopedBuildings.length - points.length;
  const fitPoints = fitPointsForScope(points, buildingId);
  const scopeKey = inventoryScopeKey({
    buildingId,
    groupId,
    city: filter.city,
    type: filter.type,
    status: filter.status,
  });

  return (
    <div className={cn(cardBase, "p-4 h-full flex flex-col")}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold tracking-tight">
            {copy.mapTitle}
          </h3>
          <p className="text-[11px] text-muted-foreground">
            {copy.mapSubtitle}
          </p>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
          <Legend color={tone.ok} label={copy.mapLegendOkLabel} />
          <Legend color={tone.warn} label={copy.mapLegendWarnLabel} />
        </div>
      </div>

      <div className="relative flex-1 min-h-[420px] rounded-lg overflow-hidden">
        <BuildingClusterMap
          bare
          className="h-full"
          mapClassName="h-full rounded-lg"
          points={points}
          fitPoints={fitPoints}
          scopeKey={scopeKey}
          emptyFit={buildingId ? "leave" : "overview"}
          rankColorVars={RANK_COLOR_VARS}
          themeKey={`${palette}-${mode}`}
          onSelectBuilding={(id) => {
            if (id === buildingId) return;
            setBuildingId(id);
          }}
        />

        {loading && points.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-card/50 backdrop-blur-sm rounded-lg pointer-events-none">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        <div className="absolute bottom-3 left-3 flex items-center gap-1.5 text-[10px] text-muted-foreground bg-card/80 backdrop-blur rounded px-1.5 py-0.5 pointer-events-none">
          <MapPin className="h-3 w-3" />
          {`${totalBuildings} kohdetta · ${points.length} kartalla${
            missingCoordCount > 0
              ? ` · ${missingCoordCount} ilman koordinaatteja`
              : ""
          } · klikkaa palloa rajataksesi`}
        </div>
        {isFiltered && (
          <button
            onClick={reset}
            className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-md border border-border bg-card/80 backdrop-blur px-2 py-1 text-[11px] hover:border-primary/40 transition"
          >
            <X className="h-3 w-3" />
            Tyhjennä rajaus
          </button>
        )}
      </div>
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="h-2 w-2 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}
